import { Tag } from '@carbon/react';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import {
  LIFECYCLE_STATUS_CONFIG,
  type LifecycleStatus,
  type LifecycleSubject,
} from './lifecycle-status';
import styles from './lifecycle-status-tag.module.scss';

const SUBJECT_CLASS: Record<LifecycleSubject, string> = {
  'type-curve': styles.typeCurveStatusTag,
  'dca': styles.dcaStatusTag,
};

export interface LifecycleStatusTagProps {
  'status': LifecycleStatus;
  /** Type Curve or DCA decline. Omit for any other record tagged with the same words. */
  'subject'?: LifecycleSubject;
  'className'?: string;
  /** For a tag kept in the layout but hidden, so a column holds one width. */
  'aria-hidden'?: boolean;
}

/**
 * A small Carbon tag naming a lifecycle status: Promoted green, Draft yellow, Archived red,
 * Default blue. Every Promoted, Draft, Archived or Default tag in the app renders through this, so
 * the words keep one colour; a test fails on a hand-written one. Type Curves and DCA declines use
 * TypeCurveStatusTag or DcaStatusTag, which derive the status from the record.
 *
 * @param {LifecycleStatusTagProps} props - Component props.
 * @return {JSX.Element} The tag.
 */
export function LifecycleStatusTag({
  status,
  subject,
  className,
  'aria-hidden': ariaHidden,
}: LifecycleStatusTagProps) {
  const config = LIFECYCLE_STATUS_CONFIG[status];
  return (
    <Tag
      size="sm"
      type={config.type}
      data-lifecycle-status={status}
      data-testid={`lifecycle-status-${status}`}
      aria-hidden={ariaHidden}
      className={classNames(
        subject ? SUBJECT_CLASS[subject] : styles.statusTag,
        { [styles.yellow]: config.yellow },
        className
      )}
    >
      {/* Carbon spends Tag's own `title` on the dismiss button and titles the label with its text,
          so the explanation rides on an inner span, the innermost title the browser shows. */}
      <span title={(subject && config.description[subject]) || config.label}>
        {config.label}
      </span>
    </Tag>
  );
}

/**
 * A name with its lifecycle tag after it. The name truncates with an ellipsis; the tag keeps its
 * width. Renders the name alone when there is no tag.
 *
 * @param {object} props - Component props.
 * @param {ReactNode} props.children - The name.
 * @param {ReactNode} props.tag - The tag, or nothing.
 * @param {string} [props.className] - Extra class on the wrapper.
 * @return {JSX.Element} The name and tag.
 */
export function NameWithStatusTag({
  children,
  tag,
  className,
}: {
  children: ReactNode;
  tag: ReactNode;
  className?: string;
}) {
  return (
    <span className={classNames(styles.withTag, className)}>
      <span className={styles.withTagName}>{children}</span>
      {tag}
    </span>
  );
}
